import React from 'react';
import { applyManualDiscounts, discountLineLabel } from '../lib/manualDiscounts';

// v3.5.99.7.23 — Manual Discounts editor.
//
// Lets a rep attach one or more discount lines to a recurring quote. Each
// line is a percentage of the contract MRR or a flat $/month, with a note
// explaining why (e.g. "$50 Discounted Monthly Firewall"). Every line shows
// on the proposal and the signed Service Order.
//
// Fully controlled: the parent owns the array (persisted to
// quotes.inputs.manualDiscounts) and passes baseMRR (the pre-discount
// contract MRR) so the card can preview each line's $ impact and the net.
//
// The component intentionally carries NO margin logic — the quote page owns
// the GM warning (it knows cost) so this stays a focused editor.

const fmt$ = (n) =>
  n != null
    ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

export default function ManualDiscounts({ value, onChange, baseMRR = 0 }) {
  const rows = Array.isArray(value) ? value : [];
  const resolved = applyManualDiscounts(baseMRR, rows);

  const update = (i, patch) =>
    onChange(rows.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const add = () =>
    onChange([...rows, { type: 'fixed', value: '', note: '' }]);
  const remove = (i) =>
    onChange(rows.filter((_, idx) => idx !== i));

  const inputBase = {
    fontSize: 12, padding: '5px 7px', border: '1px solid #d1d5db',
    borderRadius: 4, color: '#0f1e3c', background: '#fff', boxSizing: 'border-box',
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: rows.length ? 8 : 0 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0f1e3c' }}>🏷 Manual Discounts</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
            Reduce the monthly recurring. Each line appears on the proposal &amp; Service Order.
          </div>
        </div>
        <button
          type="button"
          onClick={add}
          style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', background: '#0f1e3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          + Add Discount
        </button>
      </div>

      {rows.map((d, i) => {
        const type = d.type === 'pct' ? 'pct' : 'fixed';
        const amount = type === 'pct' ? (Number(baseMRR) || 0) * ((Number(d.value) || 0) / 100) : (Number(d.value) || 0);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <select
              value={type}
              onChange={(e) => update(i, { type: e.target.value })}
              style={{ ...inputBase, width: 58, flex: '0 0 auto', cursor: 'pointer' }}
              title="Discount type"
            >
              <option value="fixed">$/mo</option>
              <option value="pct">%</option>
            </select>
            <input
              type="number"
              min="0"
              step={type === 'pct' ? '1' : '5'}
              value={d.value ?? ''}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder={type === 'pct' ? '10' : '50'}
              style={{ ...inputBase, width: 72, flex: '0 0 auto', textAlign: 'right' }}
            />
            <input
              type="text"
              value={d.note ?? ''}
              onChange={(e) => update(i, { note: e.target.value })}
              placeholder="Reason — e.g. Discounted Monthly Firewall"
              style={{ ...inputBase, flex: '1 1 auto', minWidth: 0 }}
            />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', width: 64, textAlign: 'right', flex: '0 0 auto' }}>
              −{fmt$(amount)}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              title="Remove discount"
              style={{ fontSize: 14, lineHeight: 1, padding: '2px 7px', background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', flex: '0 0 auto' }}
            >
              ×
            </button>
          </div>
        );
      })}

      {resolved.lines.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', marginTop: 4, paddingTop: 6 }}>
          <span style={{ fontSize: 10, color: '#6b7280' }}>
            {resolved.lines.length} discount{resolved.lines.length > 1 ? 's' : ''} ·
            {' '}{resolved.lines.map(discountLineLabel).join(' + ')}
          </span>
          <span style={{ fontSize: 11, color: '#0f1e3c' }}>
            <strong style={{ color: '#b91c1c' }}>−{fmt$(resolved.total)}/mo</strong>
            {Number(baseMRR) > 0 && (
              <span style={{ color: '#6b7280' }}> · Net MRR <strong>{fmt$(resolved.finalMRR)}</strong></span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
